import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, ArrowRight, TrendingUp, School, BookOpen, MapPin } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../hooks/useAuth';
import { trackEvent } from '../utils/posthog';

const CutoffTrends: React.FC = () => {
  const [cutoffs, setCutoffs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStream, setSelectedStream] = useState('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      const pageHeight = window.innerHeight;
      const currentScroll = window.scrollY;
      const scrollCount = Math.floor(currentScroll / pageHeight);

      if (scrollCount >= 1 && !user && !showSignUpPrompt) {
        setShowSignUpPrompt(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [user, showSignUpPrompt]);

  useEffect(() => {
    const fetchCutoffs = async () => {
      try {
        setIsLoading(true);
        
        // Track page view in analytics
        trackEvent('view_cutoff_trends', {
          is_authenticated: !!user
        });

        // First fetch colleges to get their names
        const { data: collegesData, error: collegesError } = await supabase
          .from('colleges')
          .select('id, name, city, stream')
          .in('city', ['Mumbai', 'Pune']) // Only Mumbai and Pune
          .limit(user ? 100 : 20); // Limit to 20 colleges for non-authenticated users

        if (collegesError) throw collegesError;

        // Create a map of college IDs to college data
        const collegesMap = new Map();
        collegesData?.forEach(college => {
          collegesMap.set(college.id, college);
        });

        // Then fetch cutoff marks
        const { data: cutoffData, error: cutoffError } = await supabase
          .from('cutoff_marks')
          .select('id, college_id, year, round_number, category, marks')
          .order('year', { ascending: false })
          .limit(user ? 100 : 20);

        if (cutoffError) throw cutoffError;

        // Combine the data
        const combinedData = cutoffData?.map(cutoff => ({
          ...cutoff,
          college: collegesMap.get(cutoff.college_id) || {
            name: 'Unknown College',
            city: 'Unknown City',
            stream: 'Unknown Stream'
          }
        })) || [];

        setCutoffs(combinedData);
      } catch (error) {
        console.error('Error fetching cutoffs:', error);
        
        // Track error in analytics
        trackEvent('error_cutoff_trends', {
          error_message: error instanceof Error ? error.message : 'Unknown error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCutoffs();
  }, [user]);

  const uniqueStreams = Array.from(new Set(cutoffs.map(c => c.college?.stream))).filter(Boolean);
  const years = Array.from(new Set(cutoffs.map(c => c.year))).sort((a, b) => b - a);

  const filteredCutoffs = cutoffs.filter(cutoff => 
    (!selectedStream || cutoff.college?.stream === selectedStream) &&
    (!selectedYear || cutoff.year === selectedYear)
  );

  const handleStreamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStream(e.target.value);
    trackEvent('filter_cutoff_trends', { filter_type: 'stream', value: e.target.value });
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(e.target.value));
    trackEvent('filter_cutoff_trends', { filter_type: 'year', value: e.target.value });
  };

  const handleSignUpClick = () => {
    trackEvent('click_signup_from_cutoff_trends');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Cutoff Trends</h1>
                <p className="text-gray-600">
                  Analyze historical cutoff patterns across colleges in Mumbai and Pune
                </p>
              </div>
              
              {!user && (
                <div className="mt-4 md:mt-0">
                  <Link 
                    to="/register" 
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#7B4AE2] hover:bg-[#6A3ACF]"
                    onClick={handleSignUpClick}
                  >
                    Sign Up for Full Access
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              )}
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <select
                  value={selectedStream}
                  onChange={handleStreamChange}
                  className="block w-full rounded-lg border-gray-300 focus:ring-2 focus:ring-[#7B4AE2] focus:border-transparent"
                >
                  <option value="">All Streams</option>
                  {uniqueStreams.map(stream => (
                    <option key={stream} value={stream}>{stream}</option>
                  ))}
                </select>

                <select
                  value={selectedYear}
                  onChange={handleYearChange}
                  className="block w-full rounded-lg border-gray-300 focus:ring-2 focus:ring-[#7B4AE2] focus:border-transparent"
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B4AE2]"></div>
                </div>
              ) : filteredCutoffs.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-medium text-gray-900">No cutoff data found</h3>
                  <p className="mt-2 text-gray-500">Try adjusting your filters or sign up for access to more data.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            College Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Stream
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Year
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Round
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Category
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cutoff Marks
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredCutoffs.map((cutoff) => (
                          <tr key={cutoff.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 bg-[#F0E9FF] rounded-lg flex items-center justify-center">
                                  <School className="h-5 w-5 text-[#7B4AE2]" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{cutoff.college?.name || 'Unknown College'}</div>
                                  <div className="text-sm text-gray-500">{cutoff.college?.city || 'Unknown City'}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                {cutoff.college?.stream || 'Unknown Stream'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {cutoff.year}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              Round {cutoff.round_number}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                                {cutoff.category}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {cutoff.marks}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {!user && (
                    <div className="mt-8 bg-[#F0E9FF] rounded-lg p-6 text-center">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Access Complete Cutoff Data
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Sign up to view our complete database of historical cutoffs and get personalized predictions.
                      </p>
                      <Link
                        to="/register"
                        className="inline-flex items-center px-6 py-3 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
                        onClick={handleSignUpClick}
                      >
                        Sign Up Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Cutoff Trends Visualization */}
            {filteredCutoffs.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4">Cutoff Trends Analysis</h2>
                <div className="flex items-center mb-6">
                  <TrendingUp className="h-5 w-5 text-[#7B4AE2] mr-2" />
                  <span className="text-gray-700">
                    Showing trends for {selectedStream ? selectedStream : 'All Streams'} in {selectedYear}
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium mb-3">Top Colleges by Cutoff</h3>
                    <div className="space-y-3">
                      {filteredCutoffs
                        .sort((a, b) => b.marks - a.marks)
                        .slice(0, 5)
                        .map((cutoff, index) => (
                          <div key={cutoff.id} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex justify-between items-center mb-1">
                              <span className="font-medium text-gray-900">
                                {index + 1}. {cutoff.college?.name}
                              </span>
                              <span className="text-sm font-bold text-[#7B4AE2]">
                                {cutoff.marks}%
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 flex items-center">
                              <MapPin className="h-3 w-3 mr-1" />
                              {cutoff.college?.city} | {cutoff.college?.stream}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium mb-3">Category-wise Analysis</h3>
                    <div className="space-y-3">
                      {Array.from(new Set(filteredCutoffs.map(c => c.category)))
                        .map(category => {
                          const categoryCutoffs = filteredCutoffs.filter(c => c.category === category);
                          const avgCutoff = categoryCutoffs.reduce((sum, c) => sum + c.marks, 0) / categoryCutoffs.length;
                          
                          return (
                            <div key={category} className="bg-gray-50 p-3 rounded-lg">
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-medium text-gray-900">{category}</span>
                                <span className="text-sm font-bold text-[#7B4AE2]">
                                  Avg: {avgCutoff.toFixed(1)}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-[#7B4AE2] h-2 rounded-full" 
                                  style={{ width: `${avgCutoff}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cutoff Prediction Section - Only for authenticated users */}
            {user && filteredCutoffs.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-4">Cutoff Predictions for 2025</h2>
                <p className="text-gray-600 mb-6">
                  Based on historical trends, here are our predictions for the upcoming academic year.
                </p>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          College Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Stream
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Predicted Cutoff
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Confidence
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Array.from(new Set(filteredCutoffs.map(c => c.college_id)))
                        .slice(0, 5)
                        .map((collegeId) => {
                          const collegeCutoffs = filteredCutoffs.filter(c => c.college_id === collegeId);
                          const college = collegeCutoffs[0]?.college;
                          const avgCutoff = collegeCutoffs.reduce((sum, c) => sum + c.marks, 0) / collegeCutoffs.length;
                          // Add a small random variation for the prediction
                          const predictedCutoff = avgCutoff + (Math.random() * 2 - 1);
                          
                          return (
                            <tr key={collegeId} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{college?.name}</div>
                                <div className="text-xs text-gray-500">{college?.city}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                  {college?.stream}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-bold text-[#7B4AE2]">
                                  {predictedCutoff.toFixed(1)}%
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                    <div 
                                      className="bg-green-500 h-2 rounded-full" 
                                      style={{ width: `${70 + Math.random() * 20}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs text-gray-500">High</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {showSignUpPrompt && !user && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transform transition-transform duration-500 ease-in-out translate-y-0 z-50">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-bold text-gray-900">
                  Want to see more cutoff data?
                </h3>
                <p className="text-gray-600 mt-1">
                  Sign up to access our complete database of historical cutoffs and predictions
                </p>
              </div>
              <div className="flex gap-4">
                <Link
                  to="/register"
                  className="inline-flex items-center px-6 py-3 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
                  onClick={handleSignUpClick}
                >
                  Sign Up Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <button
                  onClick={() => setShowSignUpPrompt(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CutoffTrends;